/**
 * ZoomControlBar - Reusable zoom control component
 * 
 * Features:
 * - Zoom in/out controls
 * - Zoom slider
 * - Actual size (100%) button
 * - Customizable zoom range
 * - View mode management (actual-size / custom)
 * 
 * Used in:
 * - RichTextEditor (template content editing)
 * - DocumentPreview (template preview)
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Slider,
  Tooltip,
  Button
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CropPortrait as ActualSizeIcon
} from '@mui/icons-material';

const ZoomControlBar = ({
  zoom = 100,
  onZoomChange,
  onZoomIn,
  onZoomOut,
  onActualSize,
  viewMode = 'actual-size', // 'actual-size' | 'custom'
  minZoom = 5,
  maxZoom = 250,
  step = 15,
  showActualSizeButton = true,
  className = ''
}) => {
  const handleZoomChange = (event, newValue) => {
    if (onZoomChange) {
      onZoomChange(newValue);
    }
  };

  const handleZoomIn = () => {
    if (onZoomIn) {
      onZoomIn();
    } else if (onZoomChange) {
      onZoomChange(Math.min(zoom + step, maxZoom));
    }
  };

  const handleZoomOut = () => {
    if (onZoomOut) {
      onZoomOut();
    } else if (onZoomChange) {
      onZoomChange(Math.max(zoom - step, minZoom));
    }
  };

  const handleActualSize = () => {
    if (onActualSize) {
      onActualSize();
    } else if (onZoomChange) {
      onZoomChange(100);
    }
  };

  return (
    <Box
      className={`rich-text-editor-zoom-bar ${className}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 12px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
        gap: 2,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 200,
        marginBottom: '8px'
      }}
    >
      {/* View Mode Button - 100% */}
      {showActualSizeButton && (
        <Tooltip title="Actual Size (Gerçek boyut - 100%)">
          <Button
            onClick={handleActualSize}
            variant={viewMode === 'actual-size' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<ActualSizeIcon />}
          >
            100%
          </Button>
        </Tooltip>
      )}

      {/* Zoom Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 250 }}>
        <Tooltip title="Zoom Out">
          <IconButton 
            size="small" 
            onClick={handleZoomOut} 
            disabled={zoom <= minZoom}
          >
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Slider
          value={zoom}
          onChange={handleZoomChange}
          min={minZoom}
          max={maxZoom}
          step={step}
          valueLabelDisplay="off"
          sx={{ flex: 1 }}
        />
        <Tooltip title="Zoom In">
          <IconButton 
            size="small" 
            onClick={handleZoomIn} 
            disabled={zoom >= maxZoom}
          >
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 50, textAlign: 'center', fontSize: '14px', fontWeight: 500 }}>
          {zoom}%
        </Box>
      </Box>
    </Box>
  );
};

ZoomControlBar.propTypes = {
  zoom: PropTypes.number,
  onZoomChange: PropTypes.func,
  onZoomIn: PropTypes.func,
  onZoomOut: PropTypes.func,
  onActualSize: PropTypes.func,
  viewMode: PropTypes.oneOf(['actual-size', 'custom']),
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  step: PropTypes.number,
  showActualSizeButton: PropTypes.bool,
  className: PropTypes.string
};

ZoomControlBar.defaultProps = {
  zoom: 100,
  viewMode: 'actual-size',
  minZoom: 5,
  maxZoom: 250,
  step: 15,
  showActualSizeButton: true,
  className: ''
};

export default ZoomControlBar;

